import { PushSubscriptionInput } from '@rp/contracts'

export const SERVICE_WORKER_READY_TIMEOUT_MS = 10_000

const PERMISSION_DENIED = 'NotAllowedError'
const BASE64_BLOCK = 4

export type PushSetupFailure = 'not_ready' | 'denied' | 'service' | 'invalid'

export class PushSetupError extends Error {
  readonly reason: PushSetupFailure

  constructor(reason: PushSetupFailure) {
    super(`push setup failed: ${reason}`)
    this.reason = reason
  }
}

export const pushSupported = (): boolean =>
  'serviceWorker' in navigator && typeof PushManager !== 'undefined' && typeof Notification !== 'undefined'

export const base64UrlToBytes = (value: string): Uint8Array<ArrayBuffer> => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64.padEnd(Math.ceil(base64.length / BASE64_BLOCK) * BASE64_BLOCK, '='))
  return Uint8Array.from(binary, (char) => char.charCodeAt(0))
}

const activeRegistration = (): Promise<ServiceWorkerRegistration> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new PushSetupError('not_ready')), SERVICE_WORKER_READY_TIMEOUT_MS)
    navigator.serviceWorker.ready.then(
      (registration) => {
        clearTimeout(timer)
        resolve(registration)
      },
      () => {
        clearTimeout(timer)
        reject(new PushSetupError('not_ready'))
      },
    )
  })

const readyRegistration = async (): Promise<ServiceWorkerRegistration> => {
  const registered = await navigator.serviceWorker.getRegistration()
  if (registered === undefined) throw new PushSetupError('not_ready')
  return activeRegistration()
}

export const currentPushSubscription = async (): Promise<PushSubscription | null> =>
  (await readyRegistration()).pushManager.getSubscription()

const sameKey = (subscription: PushSubscription, key: Uint8Array): boolean => {
  const existing = subscription.options.applicationServerKey
  if (existing === null) return false
  const bytes = new Uint8Array(existing)
  return bytes.length === key.length && bytes.every((byte, index) => byte === key[index])
}

const freshSubscription = async (manager: PushManager, key: Uint8Array<ArrayBuffer>): Promise<PushSubscription> => {
  const existing = await manager.getSubscription()
  if (existing !== null && sameKey(existing, key)) return existing
  if (existing !== null) await existing.unsubscribe()
  try {
    return await manager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
  } catch (error) {
    throw new PushSetupError(error instanceof DOMException && error.name === PERMISSION_DENIED ? 'denied' : 'service')
  }
}

export const subscribePush = async (publicKey: string): Promise<PushSubscriptionInput> => {
  const registration = await readyRegistration()
  const subscription = await freshSubscription(registration.pushManager, base64UrlToBytes(publicKey))
  const parsed = PushSubscriptionInput.safeParse(subscription.toJSON())
  if (!parsed.success) throw new PushSetupError('invalid')
  return parsed.data
}
